/**
 * Engine Atlas title block — synthesized one-paragraph position summary per
 * scope doc §5. Gravitational center of the surface; what visitors read first;
 * map below read in light of it.
 *
 * Renders substrate-driven from current disk state via fetchTitleBlockSubstrate
 * on mount. Composition done via composeTitleBlock (clause-by-clause conditional
 * per D5 — accommodates substrate-state variants without restructure).
 *
 * Visual weight per scope doc §5 — generous size, centerpiece treatment. text-base
 * + bg-surface-1 + border + p-6 anchors visual prominence vs the surrounding
 * page content (subdued BoundaryNote / ReadingThisMap / placeholder regions).
 * NOT text-xl (h1-altitude reserved for page title).
 *
 * Loading state: subtle skeleton (3 placeholder lines) — non-blank per build
 * prompt §3.
 * Error state: render fallback prose "Engine state currently unavailable." —
 * does not crash.
 *
 * Per-sentence composability slot markers reserved in DOM (HTML comments) so
 * post-Day-92 wire-up has anchor points for "changed N days ago" indicators
 * per scope doc §2 + §5.
 */

import { useEffect, useState, type JSX } from 'react';
import {
  composeTitleBlock,
  fetchTitleBlockSubstrate,
  type TitleBlockSubstrate,
} from '../../lib/atlas/titleBlockSubstrate';

export default function TitleBlock(): JSX.Element {
  const [substrate, setSubstrate] = useState<TitleBlockSubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchTitleBlockSubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <section className="bg-surface-1 border border-border rounded-lg p-6">
        <p className="text-base text-ink-secondary">Engine state currently unavailable.</p>
      </section>
    );
  }

  if (!substrate) {
    return (
      <section
        aria-label="Loading engine state"
        className="bg-surface-1 border border-border rounded-lg p-6 space-y-2"
      >
        <div className="h-4 bg-surface-4 rounded animate-pulse w-3/4" />
        <div className="h-4 bg-surface-4 rounded animate-pulse" />
        <div className="h-4 bg-surface-4 rounded animate-pulse w-2/3" />
      </section>
    );
  }

  const sentences = composeTitleBlock(substrate);

  return (
    <section className="bg-surface-1 border border-border rounded-lg p-6">
      <p className="text-base text-ink-primary leading-relaxed">
        {sentences.sentence1}
        {/* COMPOSABILITY: per-sentence "changed N days ago" inline indicator slot — wires post-Day 92 */}{' '}
        {sentences.sentence2}
        {/* COMPOSABILITY: per-sentence "changed N days ago" inline indicator slot — wires post-Day 92 */}{' '}
        {sentences.sentence3}
        {/* COMPOSABILITY: per-sentence "changed N days ago" inline indicator slot — wires post-Day 92 */}
      </p>
    </section>
  );
}
